import React, { useEffect, useRef, useState, ReactElement } from 'react';
import { isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';
import { DocumentNode, useApolloClient, useQuery } from '@apollo/client';
import { default as classnames, default as classNames } from 'classnames';

import Button from 'components/Button/new';
import Chips from 'components/Chip';
import CheckboxGroupInput from 'components/Inputs/CheckboxGroupInput';
import ChoiceInput from 'components/Inputs/ChoiceInput';
import TextInput from 'components/Inputs/TextInput';
import LoadingMessage from 'components/LoadingMessage';
import { ApiClients } from 'constants/apiClients';
import { I18N_PLATFORM_COMMON_WORD_PATH } from 'constants/i18n';
import { usersClientWithOkta } from 'graphql/usersClient';
import { IdName } from 'interfaces/IdName';
import { ErrorLogger } from 'services/ErrorLogger';

import styles from './DropdownFilterItems.module.scss';

type Props = {
  mapper: (queryResultData: any) => any[];
  graphqlQuery: DocumentNode;
  onChange: (newValue: any, data?: any) => void;

  clientSideFilter?: (searchedText: string, items: any[]) => any[];
  fullWidth?: boolean;
  graphqlClient?: ApiClients;
  graphqlVariables?: (searchText?: string) => {};
  groupBySubfield?: string;
  isMultipleSelect?: boolean;
  isCustomKeyName?: boolean;
  isVisible?: boolean;
  itemRenderer?: (item: any, searchedText?: string) => ReactElement;
  placeholder?: string;
  renderFooterSelection?: boolean;
  selectedValue?: any;
  shouldShowInput?: boolean;
  wrapperClassName?: string;
  selectAllOption?: {
    value: string;
    isVisible: boolean;
    selectAllByDefault?: (allItems: IdName[]) => void;
  };
  actionButton?: {
    value: string;
    isVisible: boolean;
    isDisabled: boolean;
    onClick?: () => void;
  };
  onActionButtonClick?: () => void;
  skipQuery?: boolean;
  optionsWrapperClassName?: string;
  customFooterComponent?: React.ReactNode;
  labelFieldName?: string;
};

const DropdownFilterItems: React.FC<Props> = props => {
  const propertiesClient = useApolloClient();
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const {
    clientSideFilter,
    fullWidth,
    graphqlQuery,
    graphqlVariables,
    groupBySubfield,
    isMultipleSelect,
    isVisible,
    itemRenderer,
    mapper,
    onChange,
    placeholder,
    renderFooterSelection,
    isCustomKeyName,
    selectedValue,
    shouldShowInput,
    selectAllOption,
    actionButton,
    wrapperClassName,
    onActionButtonClick,
    skipQuery,
    optionsWrapperClassName,
    customFooterComponent,
    labelFieldName,
  } = props;

  const [multiselectHeight, setMultiselectHeight] = useState(0);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (!isVisible || !optionsContainerRef.current || !!multiselectHeight) {
      return;
    }

    setMultiselectHeight(optionsContainerRef.current.clientHeight);
  }, [optionsContainerRef, isVisible, multiselectHeight]);

  useEffect(() => {
    if (!isVisible || !optionsContainerRef.current || !multiselectHeight) {
      return;
    }

    let chipsHeight = 0;
    if (chipsRef.current) {
      const chipsBoundingRect = chipsRef.current.getBoundingClientRect();
      const chipsMargin = 4;
      chipsHeight = chipsBoundingRect.height - chipsMargin;
      chipsRef.current.style.bottom = `-${chipsHeight}px`;
    }
  }, [chipsRef, selectedValue, isVisible, multiselectHeight]);

  const { data, error, loading } = useQuery(graphqlQuery, {
    variables: graphqlVariables?.(searchText) || {},
    fetchPolicy: 'cache-and-network',
    skip: skipQuery,
    client:
      props.graphqlClient === ApiClients.users
        ? usersClientWithOkta
        : propertiesClient,
  });

  if (error) {
    ErrorLogger.log(error.message);
  }

  const items = !!data ? mapper(data) : [];

  const clientSideFilteredItems = clientSideFilter
    ? clientSideFilter(searchText, items)
    : items;

  const renderChips = () => {
    if (!renderFooterSelection || !isMultipleSelect || !selectedValue?.length) {
      return null;
    }

    return (
      <div ref={chipsRef} className={styles['chips-container']}>
        <Chips items={selectedValue} onChange={onChange} />
      </div>
    );
  };

  const renderOptions = () => {
    if (error) {
      console.error(error);
      return (
        <p>{`${t(`${I18N_PLATFORM_COMMON_WORD_PATH}.unableToLoadItems`)}`}</p>
      );
    }

    if (loading) return <LoadingMessage />;

    if (itemRenderer) {
      return clientSideFilteredItems.map(item =>
        itemRenderer(item, searchText),
      );
    }

    if (!clientSideFilteredItems.length) {
      return (
        <p className={styles['empty-message']}>
          {`${t(`${I18N_PLATFORM_COMMON_WORD_PATH}.noItemsFound`)}`}
        </p>
      );
    }

    if (isMultipleSelect) {
      return (
        <CheckboxGroupInput
          isVertical
          size={'regular'}
          isCustomKeyName={isCustomKeyName}
          containerWrapperClassName={classNames(
            styles['option-items-container'],
            optionsWrapperClassName,
          )}
          itemWrapperClassName={styles['choice-item']}
          selectedItems={selectedValue || []}
          data={clientSideFilteredItems}
          onChange={items => onChange(items, data)}
          groupBy={groupBySubfield}
          selectAllOption={selectAllOption}
          noGroupSpaces
          labelFieldName={labelFieldName}
        />
      );
    } else {
      return (
        <ChoiceInput
          isVertical
          size={'regular'}
          containerWrapperClassName={styles['option-items-container']}
          itemWrapperClassName={styles['choice-item']}
          data={clientSideFilteredItems}
          selectedItem={selectedValue}
          onChange={onChange}
          labelFieldName={labelFieldName}
        />
      );
    }
  };

  const renderActionButton = () =>
    actionButton?.isVisible && (
      <div className={styles['action-button-container']}>
        <Button
          fullWidth
          label={actionButton.value}
          onClick={() => {
            actionButton.onClick?.();
            onActionButtonClick?.();
          }}
          disabled={actionButton.isDisabled}
        />
      </div>
    );

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={classnames(wrapperClassName, styles.container, {
        [styles['full-width']]: fullWidth,
      })}
    >
      <div className={styles['items-container']}>
        {!isUndefined(shouldShowInput) ? (
          shouldShowInput
        ) : (
          <div className={styles['search-input-container']}>
            <TextInput
              placeholder={placeholder}
              inputClassName={styles['search-input']}
              onChange={setSearchText}
              value={searchText}
              fullWidth
            />
          </div>
        )}
        <div ref={optionsContainerRef} className={styles['options-container']}>
          {renderOptions()}
        </div>
      </div>
      {renderChips()}
      {renderActionButton()}
      {customFooterComponent}
    </div>
  );
};

export default DropdownFilterItems;
